import React, { useState } from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import NewBadge from 'src/components/NewBadge/NewBadge';
import Button from 'src/components/Button';
import Callout from 'src/components/InformationCallout/Callout';
import ContactPickerMultiple from '../ContactPicker/ContactPickerMultiple';
import useScreenSize from 'src/SharedCustomHooks/UseScreenSize/useScreenSize';
import {
  ContactPickerMultiOrchestrator,
  SelectedContact,
  useContactPickerMultiOrchestrator,
} from '../ContactPicker/CustomHooks/useContactPickerMultiOrchestrator';
import {
  SaveSubscribersResult,
  Subscriber,
} from './CustomHooks/useServiceRequestSubscribers';
import './SubscribersEditDialog.scss';

interface SubscribersEditDialogProps {
  isOpen: boolean;
  serviceRequest: any;
  subscribers: Subscriber[];
  saving: boolean;
  onSave: (userIds: string[]) => Promise<SaveSubscribersResult>;
  onClose: () => void;
}

const subscriberToSelectedContact = (sub: Subscriber): SelectedContact => {
  return {
    userId: sub.userId,
    name: sub.name,
    role: sub.role,
    profileImage: null,
  };
};

const SubscribersEditDialog = ({
  isOpen,
  serviceRequest,
  subscribers,
  saving,
  onSave,
  onClose,
}: SubscribersEditDialogProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { width } = useScreenSize();
  const isMobile: boolean = width <= 767;

  const initialSelected: SelectedContact[] = subscribers.map(
    (s: Subscriber) => subscriberToSelectedContact(s)
  );

  const assignedSpId: string | undefined = serviceRequest?.serviceProvider?.id;
  const excludeUserIds: string[] = assignedSpId ? [assignedSpId] : [];

  const orchestrator: ContactPickerMultiOrchestrator =
    useContactPickerMultiOrchestrator({
      serviceRequest,
      from: undefined,
      initialSelected,
      excludeUserIds,
    });

  const handleSave = async (): Promise<void> => {
    setErrorMessage(null);

    const userIds: string[] = orchestrator.selected.map(
      (s: SelectedContact) => s.userId
    );

    const result: SaveSubscribersResult = await onSave(userIds);

    if (!result.ok) {
      setErrorMessage(result.errorMessage ?? 'Could not save subscribers.');
      return;
    }

    onClose();
  };

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          Edit Subscribers <NewBadge className="subscribers-edit-new-badge" />
        </>
      }
      maxWidth="140rem"
      minHeight="97vh"
      maxHeight="97vh"
      footer={
        <div className="subscribers-edit-dialog-footer">
          {errorMessage && (
            <div className="subscribers-edit-dialog-error">{errorMessage}</div>
          )}
          <div className="subscribers-edit-dialog-actions">
            <Button
              color="white"
              size="small"
              label="Cancel"
              onClick={onClose}
              disabled={saving}
            />
            <Button
              color="teal"
              size="small"
              label={saving ? 'Saving...' : 'Save'}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      }
    >
      <div className="subscribers-edit-dialog-picker-host">
        {!isMobile && (
          <Callout
            variant="info"
            title="Add or remove subscribers"
            subtitle="Filter and select Service Providers or Service Managers on the left to subscribe them to this ticket. Current subscribers appear on the right — remove anyone you'd like, then save."
          />
        )}
        <ContactPickerMultiple
          isLoading={orchestrator.isLoading}
          selected={orchestrator.selected}
          groupedResults={orchestrator.groupedResults}
          availableCategories={orchestrator.availableCategories}
          availableRoles={orchestrator.availableRoles}
          searchTerm={orchestrator.searchTerm}
          selectedRoles={orchestrator.selectedRoles}
          selectedCategories={orchestrator.selectedCategories}
          subtitleVariant="role-only"
          onChangeSearchTerm={orchestrator.onChangeSearchTerm}
          onChangeSelectedRoles={orchestrator.onChangeSelectedRoles}
          onChangeSelectedCategories={orchestrator.onChangeSelectedCategories}
          onSelectContact={orchestrator.onSelectContact}
          onRemoveSelectedContact={orchestrator.onRemoveSelectedContact}
        />
      </div>
    </GenericDialogue>
  );
};

export default SubscribersEditDialog;